import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  Button,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Add } from "@material-ui/icons";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { theme } from "../constants/theme";
import {
  GeneralModal,
  Searchbar,
  SortButtonAndMenu,
} from "../components-molecules";
import { ClusteredList } from ".";
import { HeaderWithButtons } from "../components-molecules";
import {
  getVerticalsSymbols,
  addNewVertical,
  getVerticalsSymbolsWithParams,
} from "../redux/actions/getVerticals";
import { ButtonPrimary, CircularProggressIndicator, PrimaryButton } from "../components-atoms";
import { buttonHoverAndActive, textButtonHoverAndActive } from "../utils/styles/buttonStyles";

// import { setError } from "../redux/actions/error";

export const Verticals = (props: any) => {
  const classes = useStyles();
  const [sortType, setSortType] = useState("Asc");
  const [newVerticalName, setNewVerticalName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newVerticalDescription, setNewVerticalDescription] = useState("");
  const dispatch = useDispatch();
  const [openAddShopModal, setAddOpenShopModal] = useState<boolean>(false);
  const [verticalData, loading] = useSelector((state: any) => [
    state.VerticalSymbolsReducer.data,
    state.VerticalSymbolsReducer.loading,
  ]);
  const history = useHistory();
  const debounceDuration = 500;

  useEffect(() => {
    dispatch(getVerticalsSymbols());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      getVerticalsSymbolsWithParams(
        searchTerm ? searchTerm : "",
        sortType.trim() === "" ? "Asc" : sortType
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType]);

  useEffect(() => {
    debounce(() => {
      dispatch(
        getVerticalsSymbolsWithParams(
          searchTerm ? searchTerm : "",
          sortType.trim() === "" ? "Asc" : sortType
        )
      );
    }, debounceDuration)();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSubmit = () => {
    if (newVerticalName.trim() === "") {
      setNameError(true);
      return;
    }

    const body = new FormData();
    body.append(
      "json",
      JSON.stringify({
        Name: newVerticalName,
        Description: newVerticalDescription,
      })
    );
    dispatch(
      addNewVertical(body, null, (resp) => {
        setNewVerticalName("");
        setNewVerticalDescription("");
        setAddOpenShopModal(false);
        history.push(`/admin/vertical/${resp.Result}`);
      })
    );
  };

  const bottomTextCreator = useCallback(
    (item: any) =>
      `${item?.CategoryCount ?? 0} ${
        item?.CategoryCount === 1 ? "Category" : "Categories"
      } · ${item?.SubCategoryCount ?? 0} ${
        item?.SubCategoryCount === 1 ? "Sub Category" : "Sub Categories"
      } · ${item?.ProductCount ?? 0} ${
        item?.ProductCount === 1 ? "Product" : "Products"
      }`,
    []
  );

  const topTextCreator = useCallback(
    (item: any) => `${item?.Name ?? "Unnamed Vertical"}`,
    []
  );

  const pathCreator = useCallback(
    (item: any) => `/admin/vertical/${item?.Id}`,
    []
  );

  const updateList = ({ search, order }) => {
    dispatch(getVerticalsSymbolsWithParams(search ? search : "", order));
  };

  return (
    <div className={classes.container}>
      <CssBaseline />
      <div className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons title="">
          <HeaderWithButtons.ButtonArea>
            <div className={classes.mgLeftAuto}>
              <SortButtonAndMenu
                menuItems={[
                  {
                    title: "Alphabetical ascending",
                    value: "Asc",
                    handleSelect: () => {
                      setSortType("Asc");
                      updateList({
                        search: "",
                        order: "Asc",
                      });
                    },
                  },
                  {
                    title: "Alphabetical descending",
                    value: "Desc",
                    handleSelect: () => {
                      setSortType("Desc");
                      updateList({
                        search: "",
                        order: "Desc",
                      });
                    },
                  },
                ]}
                selectedSortType={sortType}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setOpenSearch(true)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
              <ButtonPrimary
                data-automation="add-vertical-button"
                variant="contained"
                color="primary"
                disableElevation
                className={classes.primaryButton}
                style={{
                  paddingLeft: props.loading ? "12px" : "16px",
                }}
                startIcon={<Add />}
                onClick={() => {
                  setAddOpenShopModal(true);
                }}
              >
                Add Vertical
              </ButtonPrimary>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={searchTerm}
          isOpen={openSearch}
          placeholder="Search Vertical"
          onCancel={() => {
            setOpenSearch(false);
            setSearchTerm("");
            updateList({
              search: "",
              order: sortType,
            });
          }}
          onChange={(event) =>
            debounce(
              (e) => setSearchTerm(e.target.value),
              debounceDuration
            )(event)
          }
        />
        {loading ? (
          <CircularProggressIndicator />
        ) : (
          <ClusteredList
            data={verticalData ?? []}
            bottomTextCreator={bottomTextCreator}
            topTextCreator={topTextCreator}
            pathCreator={pathCreator}
            headlineTypeSingular="Vertical"
            headLineTypePlural="Verticals"
          />
        )}
      </div>
      <GeneralModal
        title="Add Vertical"
        description="Start by giving vertical a name and write a short description. Once added you will be able to add categories from vertical details screen."
        visible={openAddShopModal}
        onClose={setAddOpenShopModal}
        onSubmit={handleSubmit}
        modalWidth="560px"
      >
        <GeneralModal.ContentArea>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                placeholder="Vertical Name"
                value={newVerticalName}
                variant="outlined"
                margin="normal"
                fullWidth
                id="VerticalName"
                label="Vertical Name"
                name="VerticalName"
                onChange={(event: any) =>
                  setNewVerticalName(event.target.value)
                }
                error={nameError}
                InputProps={{
                  classes: {
                    root: classes.cssOutlinedInput,
                    focused: classes.cssFocused,
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
            </Grid>
          </Grid>
        </GeneralModal.ContentArea>
        <GeneralModal.ButtonArea>
          <Button
            onClick={() => {
              setAddOpenShopModal(false);
            }}
            className={classes.cancelButton}
          >
            <Typography
              onClick={() => {
                setNameError(false);
                setNewVerticalName("");
                setNewVerticalDescription("");
                setAddOpenShopModal(false);
              }}
              variant="button"
              component="p"
              color="primary"
            >
              Cancel
            </Typography>
          </Button>
          <PrimaryButton
            disabled={!newVerticalName}
            onClick={handleSubmit}
            title="Submit"
          />
        </GeneralModal.ButtonArea>
      </GeneralModal>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    ...textButtonHoverAndActive
  },
  root: {
    flexGrow: 1,
  },
  tabContainer: {
    backgroundColor: theme.palette.background.paper,
    paddingBottom: "8px",
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  },
  mgLeftAuto: {
    display: "flex",
    flexDirection: "row",
    paddingRight: 24,
  },
  cancelButton: {
    width: "80px",
    height: "48px",
    borderRadius: "8px",
    padding: "6px 16px",
    marginRight: "8px",
    ...textButtonHoverAndActive
  },
  cssOutlinedInput: {
    display: "flex",
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,

    },
  },

  cssFocused: {},

  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
});
